import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { isValidMeetingType } from '../../entities/MeetingType';
import { BaseClient } from '../BaseClient';
import { convertApiToMeetingLinkProvider, convertMeetingLinkProviderToApi, convertMeetingTypesFilter } from './converters';
export class MeetingTypeClientBase extends BaseClient {
  constructor() {
    super(...arguments);
    _defineProperty(this, "queryMeetingTypesImpl", void 0);
    _defineProperty(this, "searchMeetingTypesImpl", void 0);
    _defineProperty(this, "getMeetingTypeImpl", void 0);
    _defineProperty(this, "countMeetingTypesImpl", void 0);
    _defineProperty(this, "createMeetingTypeImpl", void 0);
    _defineProperty(this, "updateMeetingTypeImpl", void 0);
    _defineProperty(this, "deleteMeetingTypeImpl", void 0);
  }
  async searchMeetingTypes(expression) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    const meetingTypes = await this.searchMeetingTypesByExpression(expression, false, ...args);
    if (meetingTypes.length > 0) {
      return meetingTypes;
    }
    return this.searchMeetingTypesByExpression(expression, true, ...args);
  }
  async getMeetingType(meetingTypeId) {
    try {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      const {
        meetingType
      } = await this.request(this.getMeetingTypeImpl({
        meetingTypeId
      }), ...args);
      if (!meetingType) {
        throw new Error("Can't find meetingType");
      }
      return this.convertToEntity(meetingType);
    } catch (error) {
      this.logError('getMeetingType', 'Failed to get meetingType', {
        meetingTypeId
      }, error);
      throw error;
    }
  }
  async queryMeetingTypes(query) {
    try {
      for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
        args[_key3 - 1] = arguments[_key3];
      }
      const {
        meetingTypes = [],
        pagingMetadata
      } = await this.request(this.queryMeetingTypesImpl({
        query: {
          ...query,
          ...(query.filter && {
            filter: convertMeetingTypesFilter(query.filter)
          })
        }
      }), ...args);
      const total = pagingMetadata != null && pagingMetadata.hasNext ? await this.countAllMeetingTypes(...args) : meetingTypes.length;
      return {
        meetingTypes: meetingTypes.map(this.convertToEntity),
        total
      };
    } catch (error) {
      this.logError('queryMeetingTypes', 'Failed to query meetingTypes', {
        query
      }, error);
      throw error;
    }
  }
  async updateMeetingType(meetingType) {
    try {
      const {
        id,
        name,
        calendarTokenId,
        defaultService,
        revision,
        meetingLinkProvider
      } = meetingType;
      for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
        args[_key4 - 1] = arguments[_key4];
      }
      const {
        meetingType: updatedMeetingType
      } = await this.request(this.updateMeetingTypeImpl({
        meetingType: {
          id,
          name,
          tokenId: calendarTokenId,
          default: defaultService,
          revision,
          meetingLinkProvider: convertMeetingLinkProviderToApi(meetingLinkProvider)
        },
        fieldMask: ['name', 'meetingLinkProvider']
      }), ...args);
      return this.convertToEntity(updatedMeetingType);
    } catch (error) {
      this.logError('updateMeetingType', 'Failed to update meetingType', {
        meetingType
      }, error);
      throw error;
    }
  }
  async createMeetingType(newMeetingType) {
    try {
      const {
        calendarTokenId,
        name,
        meetingLinkProvider
      } = newMeetingType;
      for (var _len5 = arguments.length, args = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
        args[_key5 - 1] = arguments[_key5];
      }
      const {
        meetingType
      } = await this.request(this.createMeetingTypeImpl({
        meetingType: {
          name,
          tokenId: calendarTokenId,
          meetingLinkProvider: convertMeetingLinkProviderToApi(meetingLinkProvider)
        }
      }), ...args);
      return this.convertToEntity(meetingType);
    } catch (error) {
      this.logError('createMeetingType', 'Failed to create meetingType', {
        newMeetingType
      }, error);
      throw error;
    }
  }
  async deleteMeetingType(meetingTypeId) {
    try {
      for (var _len6 = arguments.length, args = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
        args[_key6 - 1] = arguments[_key6];
      }
      await this.request(this.deleteMeetingTypeImpl({
        meetingTypeId
      }), ...args);
      return meetingTypeId;
    } catch (error) {
      this.logError('deleteMeetingType', 'Failed to delete meetingType', {
        meetingTypeId
      }, error);
      throw error;
    }
  }
  convertToEntity(meetingType) {
    try {
      if (!meetingType) {
        throw new Error('Invalid meetingType', {
          cause: {
            meetingType
          }
        });
      }
      const convertedMeetingType = {
        id: meetingType.id,
        name: meetingType.name,
        calendarTokenId: meetingType.tokenId,
        defaultService: meetingType.default,
        revision: meetingType.revision,
        meetingLinkProvider: convertApiToMeetingLinkProvider(meetingType.meetingLinkProvider)
      };
      if (isValidMeetingType(convertedMeetingType)) {
        return convertedMeetingType;
      }
      throw new Error('Invalid MeetingType data', {
        cause: {
          meetingType
        }
      });
    } catch (error) {
      this.logError('convertToEntity', 'Failed to convert meetingType', {
        meetingType
      }, error);
      throw error;
    }
  }
  getServiceNameForLogs() {
    return 'MeetingTypeClient';
  }
  async countAllMeetingTypes() {
    try {
      for (var _len7 = arguments.length, args = new Array(_len7), _key7 = 0; _key7 < _len7; _key7++) {
        args[_key7] = arguments[_key7];
      }
      const {
        count
      } = await this.request(this.countMeetingTypesImpl({}), ...args);
      return count || 0;
    } catch (error) {
      this.logError('countMeetingTypes', 'Failed to count meetingTypes', {}, error);
      throw error;
    }
  }
  async searchMeetingTypesByExpression(expression, fuzzy) {
    this.logInfo('searchMeetingTypesByExpression', 'start', {
      expression,
      fuzzy
    });
    const request = {
      search: {
        search: {
          expression,
          fuzzy
        },
        cursorPaging: {
          limit: 10
        }
      }
    };
    for (var _len8 = arguments.length, args = new Array(_len8 > 2 ? _len8 - 2 : 0), _key8 = 2; _key8 < _len8; _key8++) {
      args[_key8 - 2] = arguments[_key8];
    }
    const {
      meetingTypes,
      pagingMetadata
    } = await this.request(this.searchMeetingTypesImpl(request), ...args);
    if (!meetingTypes) {
      this.logError('searchMeetingTypesByExpression', 'Invalid searchMeetingTypes response', {
        pagingMetadata
      });
      throw Error(`Invalid searchMeetingTypes response.`);
    }
    if (pagingMetadata != null && pagingMetadata.hasNext) {
      this.logInfo('searchMeetingTypesByExpression', 'The search returned too many meetingTypes', {
        pagingMetadata
      });
    }
    return meetingTypes.map(this.convertToEntity);
  }
}
//# sourceMappingURL=MeetingTypeClientBase.js.map